import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
} from "reactstrap";
import {
  getPriceCatalogItems,
  getProductStockDetails,
  updatePrice,
  getPackageSeriesDetails,
  updatePackageSeriesInstallments
} from "../../components/Api/GmsPriceCatalog";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getTaxes } from "../../components/Api/GmsTax";
import { ChevronDown, ChevronRight, Save, Plus, Minus } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '300px',
    overflowY: 'auto',
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const entityTypeOptions = [
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
  { value: 'package', label: 'Package' },
  { value: 'packageSeries', label: 'Package Series' },
];

const statusOptions = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

export default function PriceCatalogPage() {
  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPriceCatalog');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPriceCatalog');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPriceCatalog');
  const isAdmin = isUserAdmin();

  // Get user session
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
      businessId: authUser.companyId || null,
      branchId: authUser.branchId || null,
    };
  };

  const session = useMemo(() => getUserSession(), []);

  // State management
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({ value: true, label: 'Active' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productStockDetails, setProductStockDetails] = useState({});
  const [loadingStock, setLoadingStock] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [packageSeriesDetails, setPackageSeriesDetails] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [installmentCount, setInstallmentCount] = useState(0);
  const [editingCostPrice, setEditingCostPrice] = useState(null);
  const [editingSalesPrice, setEditingSalesPrice] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [taxes, setTaxes] = useState([]);

  // Load businesses
  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);

      // Auto-select if user has business ID
      if (session.businessId && !selectedBusiness) {
        const userBusiness = businessData.find(b => b.business_Id === session.businessId);
        if (userBusiness) {
          setSelectedBusiness({
            value: session.businessId,
            label: userBusiness.business_NameEn || userBusiness.business_Name
          });
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  };

  // Load branches
  const loadBranches = async (businessId) => {
    if (!businessId) {
      setBranches([]);
      return;
    }
    try {
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setBranches(branchData);

      // Auto-select if user has branch ID
      if (session.branchId && !selectedBranch) {
        const userBranch = branchData.find(b => b.branch_Id === session.branchId);
        if (userBranch) {
          setSelectedBranch({
            value: session.branchId,
            label: `${userBranch.branch_NameEn} - ${userBranch.branch_NameAr}`
          });
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  // Load taxes
  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ status: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
    }
  };

  // Load price catalog items
  const loadItems = async () => {
    if (!selectedEntityType || !selectedBusiness) {
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const request = {
        Entity_Type: selectedEntityType.value,
        Business_Id: selectedBusiness.value,
        Branch_Id: selectedBranch?.value || null,
        Status: selectedStatus.value,
        PageNumber: page,
        PageSize: pageSize,
      };

      const response = await getPriceCatalogItems(request);
      // Handle both camelCase (from API) and PascalCase responses
      const items = response.items || response.Items || [];
      const totalCount = response.totalCount || response.TotalCount || 0;
      setItems(items);
      setTotalCount(totalCount);
    } catch (error) {
      console.error("Error loading price catalog items:", error);
      Swal.fire("Error", "Failed to load items", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load product stock details
  const loadProductStock = async (productId, forceReload = false) => {
    if (!forceReload && productStockDetails[productId]) {
      console.log('Skipping stock load for product', productId, '- already cached');
      return; // Already loaded, skip unless forced
    }

    try {
      console.log('Loading product stock for product:', productId, 'forceReload:', forceReload);
      setLoadingStock(prev => ({ ...prev, [productId]: true }));
      const stockData = await getProductStockDetails(
        productId,
        selectedBusiness?.value || null,
        selectedBranch?.value || null
      );
      console.log('Stock data received:', stockData);
      setProductStockDetails(prev => ({
        ...prev,
        [productId]: stockData || []
      }));
    } catch (error) {
      console.error("Error loading product stock:", error);
      Swal.fire("Error", "Failed to load product stock details", "error");
    } finally {
      setLoadingStock(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Toggle product expansion
  const toggleProductExpansion = async (productId) => {
    const isExpanded = expandedProducts[productId];
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !isExpanded
    }));

    if (!isExpanded) {
      await loadProductStock(productId);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (error) {
      return '-';
    }
  };

  // Handle price change
  const handlePriceChange = (item, field, value, batchNumber = null, stockItem = null) => {
    if (isViewOnly || !hasEditOrHigher) {
      return;
    }

    console.log('handlePriceChange called:', { item, field, value, batchNumber, stockItem });

    // Get entity ID (handle both camelCase and PascalCase)
    const entityId = item.entity_Id || item.Entity_Id;

    // If we're editing a stock batch, use stock values; otherwise use item values
    let costPrice, salesPrice;
    if (stockItem) {
      costPrice = stockItem.cost_Price !== undefined ? stockItem.cost_Price : stockItem.Cost_Price;
      salesPrice = stockItem.sales_Price !== undefined ? stockItem.sales_Price : stockItem.Sales_Price;
    } else {
      costPrice = item.cost_Price !== undefined ? item.cost_Price : item.Cost_Price;
      salesPrice = item.sales_Price !== undefined ? item.sales_Price : item.Sales_Price;
    }

    // Preserve existing editing values if we're already editing this item
    const isCurrentlyEditing = editingItem?.Entity_Id === entityId &&
      (batchNumber ? editingItem?.batchNumber === batchNumber : !editingItem?.batchNumber);

    if (isCurrentlyEditing) {
      // Preserve the other field's value from editingItem
      costPrice = field === 'Cost_Price' ? (value === '' ? '' : (parseFloat(value) || 0)) : (editingItem.Cost_Price !== undefined ? editingItem.Cost_Price : costPrice);
      salesPrice = field === 'Sales_Price' ? (value === '' ? '' : (parseFloat(value) || 0)) : (editingItem.Sales_Price !== undefined ? editingItem.Sales_Price : salesPrice);
    } else {
      // Use the new value for the changed field, existing value for the other
      const newValue = value === '' ? '' : (parseFloat(value) || 0);
      if (field === 'Cost_Price') {
        costPrice = newValue;
      } else if (field === 'Sales_Price') {
        salesPrice = newValue;
      }
    }

    // Create editing item with consistent property names
    const editingItemData = {
      Entity_Id: entityId, // Always use PascalCase for consistency
      Cost_Price: costPrice !== undefined && costPrice !== null ? costPrice : null,
      Sales_Price: salesPrice !== undefined && salesPrice !== null ? salesPrice : null,
      batchNumber: batchNumber
    };

    console.log('Setting editingItem:', editingItemData);
    setEditingItem(editingItemData);
  };

  // Validate and save price change
  const savePriceChange = async () => {
    if (!editingItem) return;

    // Check if sales price < cost price
    if (editingItem.Sales_Price && editingItem.Cost_Price &&
      editingItem.Sales_Price < editingItem.Cost_Price) {
      const result = await Swal.fire({
        title: 'Warning',
        text: 'Sales price is below cost price. Are you sure you want to proceed?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      });

      if (!result.isConfirmed) {
        setEditingItem(null);
        return;
      }
    }

    try {
      setSaveLoading(true);
      // Get entity ID from editingItem (handle both camelCase and PascalCase)
      const entityId = editingItem.Entity_Id || editingItem.entity_Id;

      const request = {
        Entity_Type: selectedEntityType.value,
        Entity_Id: entityId,
        Batch_Number: editingItem.batchNumber || null,
        New_Cost_Price: editingItem.Cost_Price !== undefined ? editingItem.Cost_Price : null,
        New_Sales_Price: editingItem.Sales_Price !== undefined ? editingItem.Sales_Price : null,
        Modified_By: session.userId,
        Business_Id: selectedBusiness?.value || null,
        Branch_Id: selectedBranch?.value || null,
      };

      await updatePrice(request);

      console.log('Price update successful, reloading data...');

      Swal.fire("Success", "Price updated successfully", "success");

      // Clear editing state
      const savedEntityId = editingItem.Entity_Id || editingItem.entity_Id;
      const wasProductBatch = selectedEntityType.value === 'product' && editingItem.batchNumber;
      setEditingItem(null);

      // Reload main items list
      await loadItems();

      // Force reload product stock if it was a product batch (to get updated prices)
      if (wasProductBatch) {
        console.log('Force reloading product stock for product:', savedEntityId);
        // Clear the cached stock data for this product to force reload
        setProductStockDetails(prev => {
          const updated = { ...prev };
          delete updated[savedEntityId];
          return updated;
        });
        // Reload with force flag
        await loadProductStock(savedEntityId, true);
        console.log('Product stock reloaded');
      }
    } catch (error) {
      console.error("Error updating price:", error);
      Swal.fire("Error", "Failed to update price", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle package series installment modal
  const openInstallmentModal = async (seriesId) => {
    try {
      setLoading(true);
      const details = await getPackageSeriesDetails(seriesId);
      console.log('Package series details received:', details);
      setPackageSeriesDetails(details);
      // Handle both camelCase and PascalCase property names
      const installmentsData = details.Installments || details.installments || [];
      console.log('Installments data:', installmentsData);
      setInstallments(installmentsData);
      setInstallmentCount(installmentsData.length);
      // Set initial editing prices
      const cost = details.Cost !== undefined ? details.Cost : details.cost;
      const amount = details.Amount !== undefined ? details.Amount : details.amount;
      setEditingCostPrice(cost !== undefined && cost !== null ? cost : 0);
      setEditingSalesPrice(amount !== undefined && amount !== null ? amount : 0);
      setShowInstallmentModal(true);
    } catch (error) {
      console.error("Error loading package series details:", error);
      Swal.fire("Error", "Failed to load package series details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Generate installments based on count and sales price
  const generateInstallments = (count, amount) => {
    if (count <= 0 || amount <= 0) {
      setInstallments([]);
      return;
    }

    const baseInstallmentAmount = Math.floor((amount / count) * 100) / 100; // Round down to 2 decimal places
    const generatedInstallments = [];
    let totalSoFar = 0;

    // Generate first (count - 1) installments with base amount
    for (let i = 1; i < count; i++) {
      generatedInstallments.push({
        InstallmentId: null, // New installment
        InstallmentName: `Installment ${i}`,
        InstallmentAmount: baseInstallmentAmount,
        installmentName: `Installment ${i}`,
        installmentAmount: baseInstallmentAmount
      });
      totalSoFar += baseInstallmentAmount;
    }

    // Last installment gets the remaining amount to ensure exact total
    const lastInstallmentAmount = Math.round((amount - totalSoFar) * 100) / 100;
    generatedInstallments.push({
      InstallmentId: null, // New installment
      InstallmentName: `Installment ${count}`,
      InstallmentAmount: lastInstallmentAmount,
      installmentName: `Installment ${count}`,
      installmentAmount: lastInstallmentAmount
    });

    setInstallments(generatedInstallments);
  };

  // Handle installment amount change
  const handleInstallmentAmountChange = (index, value) => {
    const updatedInstallments = [...installments];
    const amount = parseFloat(value) || 0;
    updatedInstallments[index] = {
      ...updatedInstallments[index],
      InstallmentAmount: amount,
      installmentAmount: amount
    };
    setInstallments(updatedInstallments);
  };

  // Remove installment
  const removeInstallment = (index) => {
    if (installments.length <= 1) {
      Swal.fire("Warning", "At least one installment is required", "warning");
      return;
    }
    const updatedInstallments = installments.filter((_, i) => i !== index);
    setInstallments(updatedInstallments);
    setInstallmentCount(updatedInstallments.length);
  };

  // Add new installment
  const addInstallment = () => {
    const newIndex = installments.length + 1;
    const newInstallment = {
      InstallmentId: null,
      InstallmentName: `Installment ${newIndex}`,
      InstallmentAmount: 0,
      installmentName: `Installment ${newIndex}`,
      installmentAmount: 0
    };
    setInstallments([...installments, newInstallment]);
    setInstallmentCount(installments.length + 1);
  };

  // Calculate net installment amount
  const calculateNetInstallmentAmount = (installmentAmount) => {
    if (!packageSeriesDetails) return 0;

    // Handle both camelCase and PascalCase property names
    const discountPercentage = packageSeriesDetails.DiscountPercentage !== undefined
      ? packageSeriesDetails.DiscountPercentage
      : (packageSeriesDetails.discountPercentage || 0);
    const taxIds = packageSeriesDetails.TaxIds || packageSeriesDetails.taxIds || [];

    const discountAmount = (installmentAmount * discountPercentage) / 100;
    const amountAfterDiscount = installmentAmount - discountAmount;
    const totalTaxPercentage = taxes
      .filter(t => taxIds.includes(t.tax_Id))
      .reduce((sum, t) => sum + (parseFloat(t.tax_Perc) || 0), 0);
    const taxAmount = (amountAfterDiscount * totalTaxPercentage) / 100;
    return Math.round((installmentAmount - discountAmount + taxAmount) * 100) / 100;
  };

  // Save installments and prices
  const saveInstallments = async () => {
    if (!packageSeriesDetails) return;

    // Handle both camelCase and PascalCase property names
    const seriesId = packageSeriesDetails.SeriesId !== undefined
      ? packageSeriesDetails.SeriesId
      : (packageSeriesDetails.seriesId || 0);

    // Validate total installments equals sales price
    const totalInstallments = installments.reduce((sum, inst) => {
      const amount = inst.InstallmentAmount !== undefined
        ? inst.InstallmentAmount
        : (inst.installmentAmount || 0);
      return sum + (parseFloat(amount) || 0);
    }, 0);

    if (Math.abs(totalInstallments - (editingSalesPrice || 0)) > 0.05) {
      Swal.fire("Error", "Total installments amount must equal the sales price", "error");
      return;
    }

    // Check if sales price < cost price
    if (editingSalesPrice && editingCostPrice && editingSalesPrice < editingCostPrice) {
      const result = await Swal.fire({
        title: 'Warning',
        text: 'Sales price is below cost price. Are you sure you want to proceed?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      setSaveLoading(true);

      // First, update prices if they changed
      const originalCost = packageSeriesDetails.Cost !== undefined ? packageSeriesDetails.Cost : packageSeriesDetails.cost;
      const originalAmount = packageSeriesDetails.Amount !== undefined ? packageSeriesDetails.Amount : packageSeriesDetails.amount;

      if (editingCostPrice !== originalCost || editingSalesPrice !== originalAmount) {
        const priceRequest = {
          Entity_Type: 'packageSeries',
          Entity_Id: seriesId,
          Batch_Number: null,
          New_Cost_Price: editingCostPrice !== null ? editingCostPrice : null,
          New_Sales_Price: editingSalesPrice !== null ? editingSalesPrice : null,
          Modified_By: session.userId,
          Business_Id: selectedBusiness?.value || null,
          Branch_Id: selectedBranch?.value || null,
        };

        await updatePrice(priceRequest);
      }

      // Then, update installments
      const request = {
        Series_Id: seriesId,
        Installments: installments.map(inst => ({
          InstallmentId: inst.InstallmentId !== undefined ? inst.InstallmentId : inst.installmentId,
          InstallmentName: inst.InstallmentName !== undefined ? inst.InstallmentName : inst.installmentName,
          InstallmentAmount: parseFloat(inst.InstallmentAmount !== undefined ? inst.InstallmentAmount : inst.installmentAmount) || 0
        })),
        Modified_By: session.userId,
      };

      await updatePackageSeriesInstallments(request);

      Swal.fire("Success", "Package series updated successfully", "success");
      setShowInstallmentModal(false);
      await loadItems();
    } catch (error) {
      console.error("Error updating package series:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to update package series", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadTaxes();
    }
  }, [hasPermission]);

  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (selectedEntityType && selectedBusiness) {
      setPage(1);
      loadItems();
    }
  }, [selectedEntityType, selectedBusiness, selectedBranch, selectedStatus]);

  useEffect(() => {
    if (selectedEntityType && selectedBusiness) {
      loadItems();
    }
  }, [page]);

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <h4>Access Denied</h4>
            <p>You do not have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Price Catalog" breadcrumbItem="Price Catalog" />

        <Card>
          <CardBody>
            {/* Filters */}
            <Row className="mb-3">
              <Col md={3}>
                <Label>Company</Label>
                <Select
                  value={selectedBusiness}
                  onChange={(option) => {
                    setSelectedBusiness(option);
                    setSelectedBranch(null);
                  }}
                  options={businesses.map(b => ({
                    value: b.business_Id,
                    label: b.business_NameEn || b.business_Name
                  }))}
                  placeholder="Select Business"
                  styles={selectStyles}
                  isDisabled={!!session.businessId}
                />
              </Col>
              <Col md={3}>
                <Label>Branch</Label>
                <Select
                  value={selectedBranch}
                  onChange={setSelectedBranch}
                  options={branches.map(b => ({
                    value: b.branch_Id,
                    label: `${b.branch_NameEn} - ${b.branch_NameAr}`
                  }))}
                  placeholder="Select Branch"
                  styles={selectStyles}
                  isDisabled={!selectedBusiness || !!session.branchId}
                />
              </Col>
              <Col md={2}>
                <Label>Status</Label>
                <Select
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  options={statusOptions}
                  styles={selectStyles}
                />
              </Col>
              <Col md={4}>
                <Label>Entity Type</Label>
                <Select
                  value={selectedEntityType}
                  onChange={setSelectedEntityType}
                  options={entityTypeOptions}
                  placeholder="Select Entity Type"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  maxMenuHeight={300}
                />
              </Col>
            </Row>

            {/* Items Table */}
            {selectedEntityType && selectedBusiness && (
              <>
                {loading ? (
                  <div className="text-center py-5">
                    <ClipLoader color="#556ee6" size={50} />
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table bordered hover>
                        <thead className="table-light">
                          <tr>
                            {selectedEntityType.value === 'product' && <th width="5%"></th>}
                            <th width="5%">Sr.No</th>
                            <th width="20%">Name</th>
                            {selectedEntityType.value !== 'product' && selectedEntityType.value !== 'packageSeries' && hasEditOrHigher && <th width="15%">Cost Price</th>}
                            {selectedEntityType.value !== 'product' && selectedEntityType.value !== 'packageSeries' && <th width="15%">Sales Price</th>}
                            {selectedEntityType.value === 'packageSeries' && hasEditOrHigher && <th width="15%">Cost Price</th>}
                            {selectedEntityType.value === 'packageSeries' && <th width="15%">Sales Price</th>}
                            <th width="15%">Last Modified</th>
                            {selectedEntityType.value !== 'product' && hasEditOrHigher && <th width="10%">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={
                                (selectedEntityType.value === 'product' ? 1 : 0) + // Expand icon for products
                                1 + // Serial number
                                1 + // Name
                                (selectedEntityType.value !== 'product' && selectedEntityType.value !== 'packageSeries' && hasEditOrHigher ? 1 : 0) + // Cost Price (if not product/packageSeries and edit permission)
                                (selectedEntityType.value !== 'product' && selectedEntityType.value !== 'packageSeries' ? 1 : 0) + // Sales Price (if not product/packageSeries)
                                (selectedEntityType.value === 'packageSeries' && hasEditOrHigher ? 1 : 0) + // Cost Price (if packageSeries and edit permission)
                                (selectedEntityType.value === 'packageSeries' ? 1 : 0) + // Sales Price (if packageSeries)
                                1 + // Last Modified
                                (selectedEntityType.value !== 'product' && hasEditOrHigher ? 1 : 0) // Actions (if not product and edit permission)
                              } className="text-center">
                                No items found
                              </td>
                            </tr>
                          ) : (
                            items.map((item, index) => {
                              const serialNumber = (page - 1) * pageSize + index + 1;
                              const isProduct = selectedEntityType.value === 'product';
                              // Handle both camelCase and PascalCase property names
                              const entityId = item.entity_Id || item.Entity_Id;
                              const entityNameEn = item.entity_NameEn || item.Entity_NameEn;
                              const costPrice = item.cost_Price !== undefined ? item.cost_Price : item.Cost_Price;
                              const salesPrice = item.sales_Price !== undefined ? item.sales_Price : item.Sales_Price;
                              const lastModifiedDate = item.last_Modified_Date || item.Last_Modified_Date;
                              const isExpanded = expandedProducts[entityId];
                              const stockDetails = productStockDetails[entityId] || [];

                              return (
                                <React.Fragment key={entityId}>
                                  {/* Main Row */}
                                  <tr>
                                    {isProduct && (
                                      <td>
                                        <Button
                                          color="link"
                                          size="sm"
                                          onClick={() => toggleProductExpansion(entityId)}
                                          className="p-0"
                                        >
                                          {isExpanded ? (
                                            <ChevronDown size={16} />
                                          ) : (
                                            <ChevronRight size={16} />
                                          )}
                                        </Button>
                                      </td>
                                    )}
                                    <td>{serialNumber}</td>
                                    <td>{entityNameEn}</td>
                                    {selectedEntityType.value !== 'product' && hasEditOrHigher && (
                                      <td>
                                        {editingItem?.Entity_Id === entityId && !editingItem?.batchNumber ? (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingItem.Cost_Price !== undefined && editingItem.Cost_Price !== null ? editingItem.Cost_Price : ''}
                                            onChange={(e) => {
                                              console.log('Cost Price onChange:', e.target.value);
                                              handlePriceChange(item, 'Cost_Price', e.target.value);
                                            }}
                                            onBlur={(e) => {
                                              console.log('Cost Price onBlur');
                                            }}
                                            style={{ width: '100px' }}
                                            autoFocus
                                          />
                                        ) : (
                                          costPrice?.toFixed(2) || '-'
                                        )}
                                      </td>
                                    )}
                                    {selectedEntityType.value !== 'product' && (
                                      <td>
                                        {editingItem?.Entity_Id === entityId && !editingItem?.batchNumber ? (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingItem.Sales_Price !== undefined && editingItem.Sales_Price !== null ? editingItem.Sales_Price : ''}
                                            onChange={(e) => {
                                              console.log('Sales Price onChange:', e.target.value);
                                              handlePriceChange(item, 'Sales_Price', e.target.value);
                                            }}
                                            onBlur={(e) => {
                                              console.log('Sales Price onBlur');
                                            }}
                                            style={{ width: '100px' }}
                                            autoFocus={editingItem?.Cost_Price !== undefined}
                                          />
                                        ) : (
                                          salesPrice?.toFixed(2) || '-'
                                        )}
                                      </td>
                                    )}
                                    <td>{formatDate(lastModifiedDate)}</td>
                                    {selectedEntityType.value !== 'product' && selectedEntityType.value !== 'packageSeries' && hasEditOrHigher && (
                                      <td>
                                        {editingItem?.Entity_Id === entityId && !editingItem?.batchNumber ? (
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={savePriceChange}
                                            disabled={saveLoading}
                                          >
                                            <Save size={14} />
                                          </Button>
                                        ) : (
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => {
                                              console.log('Edit button clicked for item:', item);
                                              const editingData = {
                                                Entity_Id: entityId,
                                                Cost_Price: costPrice !== undefined ? costPrice : null,
                                                Sales_Price: salesPrice !== undefined ? salesPrice : null,
                                                batchNumber: null
                                              };
                                              console.log('Setting editingItem to:', editingData);
                                              setEditingItem(editingData);
                                            }}
                                          >
                                            Edit
                                          </Button>
                                        )}
                                      </td>
                                    )}
                                    {selectedEntityType.value === 'packageSeries' && hasEditOrHigher && (
                                      <td>
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => openInstallmentModal(entityId)}
                                        >
                                          Edit
                                        </Button>
                                      </td>
                                    )}
                                  </tr>

                                  {/* Product Stock Details Rows */}
                                  {isProduct && isExpanded && (
                                    <>
                                      {loadingStock[entityId] ? (
                                        <tr>
                                          <td colSpan={6} className="text-center">
                                            <ClipLoader color="#556ee6" size={20} />
                                          </td>
                                        </tr>
                                      ) : stockDetails.length === 0 ? (
                                        <tr>
                                          <td colSpan={6} className="text-center text-muted">
                                            No stock available
                                          </td>
                                        </tr>
                                      ) : (
                                        stockDetails.map((stock, stockIndex) => {
                                          // Handle both camelCase and PascalCase property names
                                          const batchNumber = stock.batch_Number || stock.Batch_Number;
                                          const serialNumber = stock.serial_Number || stock.Serial_Number;
                                          const expiryDate = stock.expiry_Date || stock.Expiry_Date;
                                          const stockCostPrice = stock.cost_Price !== undefined ? stock.cost_Price : stock.Cost_Price;
                                          const stockSalesPrice = stock.sales_Price !== undefined ? stock.sales_Price : stock.Sales_Price;
                                          const stockLastModified = stock.last_Modified_Date || stock.Last_Modified_Date;

                                          return (
                                            <tr
                                              key={`${entityId}-${batchNumber}-${stockIndex}`}
                                              className="table-secondary"
                                              style={{ backgroundColor: '#e9ecef' }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#dee2e6';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                              }}
                                            >
                                              <td></td>
                                              <td>{serialNumber}</td>
                                              <td>
                                                Batch: {batchNumber || 'N/A'}
                                                {expiryDate && ` | Exp: ${expiryDate}`}
                                              </td>
                                              {hasEditOrHigher && (
                                                <td>
                                                  {editingItem?.Entity_Id === entityId &&
                                                    editingItem?.batchNumber === batchNumber ? (
                                                    <Input
                                                      type="number"
                                                      step="0.01"
                                                      value={editingItem.Cost_Price !== undefined && editingItem.Cost_Price !== null ? editingItem.Cost_Price : ''}
                                                      onChange={(e) => {
                                                        console.log('Stock Cost Price onChange:', e.target.value);
                                                        handlePriceChange(item, 'Cost_Price', e.target.value, batchNumber, stock);
                                                      }}
                                                      onBlur={(e) => {
                                                        console.log('Stock Cost Price onBlur');
                                                      }}
                                                      style={{ width: '100px' }}
                                                      autoFocus
                                                    />
                                                  ) : (
                                                    stockCostPrice?.toFixed(2) || '-'
                                                  )}
                                                </td>
                                              )}
                                              <td>
                                                {editingItem?.Entity_Id === entityId &&
                                                  editingItem?.batchNumber === batchNumber ? (
                                                  <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={editingItem.Sales_Price !== undefined && editingItem.Sales_Price !== null ? editingItem.Sales_Price : ''}
                                                    onChange={(e) => {
                                                      console.log('Stock Sales Price onChange:', e.target.value);
                                                      handlePriceChange(item, 'Sales_Price', e.target.value, batchNumber, stock);
                                                    }}
                                                    onBlur={(e) => {
                                                      console.log('Stock Sales Price onBlur');
                                                    }}
                                                    style={{ width: '100px' }}
                                                    autoFocus={editingItem?.Cost_Price !== undefined}
                                                  />
                                                ) : (
                                                  stockSalesPrice?.toFixed(2) || '-'
                                                )}
                                              </td>
                                              <td>{formatDate(stockLastModified)}</td>
                                              {hasEditOrHigher && (
                                                <td>
                                                  {editingItem?.Entity_Id === entityId &&
                                                    editingItem?.batchNumber === batchNumber ? (
                                                    <Button
                                                      color="success"
                                                      size="sm"
                                                      onClick={savePriceChange}
                                                      disabled={saveLoading}
                                                    >
                                                      <Save size={14} />
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      color="primary"
                                                      size="sm"
                                                      onClick={() => {
                                                        console.log('Edit button clicked for stock:', { item, entityId, batchNumber, stockCostPrice, stockSalesPrice });
                                                        const editingData = {
                                                          Entity_Id: entityId,
                                                          batchNumber: batchNumber,
                                                          Cost_Price: stockCostPrice !== undefined ? stockCostPrice : null,
                                                          Sales_Price: stockSalesPrice !== undefined ? stockSalesPrice : null
                                                        };
                                                        console.log('Setting editingItem to:', editingData);
                                                        setEditingItem(editingData);
                                                      }}
                                                    >
                                                      Edit
                                                    </Button>
                                                  )}
                                                </td>
                                              )}
                                            </tr>
                                          );
                                        })
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalCount > pageSize && (
                      <div className="d-flex justify-content-center mt-3">
                        <Pagination
                          current={page}
                          total={totalCount}
                          pageSize={pageSize}
                          onChange={setPage}
                          showSizeChanger={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Package Series Installment Modal */}
        <Modal isOpen={showInstallmentModal} toggle={() => setShowInstallmentModal(false)} size="lg">
          <ModalHeader toggle={() => setShowInstallmentModal(false)}>
            Edit Package Series Installments
          </ModalHeader>
          <ModalBody>
            {packageSeriesDetails && (() => {
              // Handle both camelCase and PascalCase property names
              const cost = packageSeriesDetails.Cost !== undefined ? packageSeriesDetails.Cost : packageSeriesDetails.cost;
              const amount = packageSeriesDetails.Amount !== undefined ? packageSeriesDetails.Amount : packageSeriesDetails.amount;
              const discountPercentage = packageSeriesDetails.DiscountPercentage !== undefined ? packageSeriesDetails.DiscountPercentage : packageSeriesDetails.discountPercentage;
              const netAmount = packageSeriesDetails.NetAmount !== undefined ? packageSeriesDetails.NetAmount : packageSeriesDetails.netAmount;
              const taxIds = packageSeriesDetails.TaxIds || packageSeriesDetails.taxIds || [];

              return (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label><strong>Cost Price *</strong></Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingCostPrice !== null && editingCostPrice !== undefined ? editingCostPrice : ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setEditingCostPrice(newValue);
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label><strong>Sales Price *</strong></Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingSalesPrice !== null && editingSalesPrice !== undefined ? editingSalesPrice : ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setEditingSalesPrice(newValue);
                            // Regenerate installments if count is set
                            if (installmentCount > 0 && newValue > 0) {
                              generateInstallments(installmentCount, newValue);
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Label><strong>Discount Percentage:</strong> {(discountPercentage !== undefined && discountPercentage !== null) ? discountPercentage.toFixed(2) : '0.00'}%</Label>
                    </Col>
                    <Col md={6}>
                      <Label><strong>Taxes:</strong> {
                        taxes
                          .filter(t => taxIds.includes(t.tax_Id))
                          .map(t => `${t.tax_NameEn} (${t.tax_Perc}%)`)
                          .join(', ') || 'None'
                      }</Label>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Label><strong>Net Amount:</strong> {
                        (() => {
                          if (!editingSalesPrice) return '0.00';
                          const discountAmt = (editingSalesPrice * (discountPercentage || 0)) / 100;
                          const totalTaxPerc = taxes
                            .filter(t => taxIds.includes(t.tax_Id))
                            .reduce((sum, t) => sum + (parseFloat(t.tax_Perc) || 0), 0);
                          const taxAmt = ((editingSalesPrice - discountAmt) * totalTaxPerc) / 100;
                          return (editingSalesPrice - discountAmt + taxAmt).toFixed(2);
                        })()
                      }</Label>
                    </Col>
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col md={12}>
                      <FormGroup>
                        <Label><strong>Installment Count</strong></Label>
                        <div className="d-flex gap-2 align-items-center">
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            value={installmentCount}
                            onChange={(e) => {
                              const count = parseInt(e.target.value) || 0;
                              setInstallmentCount(count);
                              if (count > 0 && editingSalesPrice > 0) {
                                generateInstallments(count, editingSalesPrice);
                              } else if (count === 0) {
                                setInstallments([]);
                              }
                            }}
                            style={{ width: '150px' }}
                            placeholder="Number of installments"
                          />
                          <Button
                            color="primary"
                            size="sm"
                            onClick={addInstallment}
                            disabled={installments.length >= 12}
                          >
                            <Plus size={14} /> Add
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th>Installment</th>
                        <th>Amount</th>
                        <th>Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {installments.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            No installments. Set installment count and sales price to generate installments.
                          </td>
                        </tr>
                      ) : (
                        installments.map((inst, index) => {
                          // Handle both camelCase and PascalCase property names
                          const installmentId = inst.InstallmentId !== undefined ? inst.InstallmentId : inst.installmentId;
                          const installmentName = inst.InstallmentName !== undefined ? inst.InstallmentName : inst.installmentName;
                          const installmentAmount = inst.InstallmentAmount !== undefined ? inst.InstallmentAmount : inst.installmentAmount;

                          return (
                            <tr key={installmentId || index}>
                              <td>{installmentName || `Installment ${index + 1}`}</td>
                              <td>
                                <div className="d-flex gap-2 align-items-center">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={installmentAmount !== undefined && installmentAmount !== null ? installmentAmount : ''}
                                    onChange={(e) => handleInstallmentAmountChange(index, e.target.value)}
                                    style={{ width: '120px' }}
                                  />
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => removeInstallment(index)}
                                    disabled={installments.length <= 1}
                                    style={{ height: '38px', width: '38px', padding: '0' }}
                                  >
                                    <Minus size={14} />
                                  </Button>
                                </div>
                              </td>
                              <td>{calculateNetInstallmentAmount(installmentAmount || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total:</th>
                        <th>{installments.reduce((sum, inst) => {
                          const amount = inst.InstallmentAmount !== undefined ? inst.InstallmentAmount : inst.installmentAmount;
                          return sum + (parseFloat(amount) || 0);
                        }, 0).toFixed(2)}</th>
                        <th>{installments.reduce((sum, inst) => {
                          const amount = inst.InstallmentAmount !== undefined ? inst.InstallmentAmount : inst.installmentAmount;
                          return sum + calculateNetInstallmentAmount(amount || 0);
                        }, 0).toFixed(2)}</th>
                      </tr>
                    </tfoot>
                  </Table>
                </>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowInstallmentModal(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={saveInstallments} disabled={saveLoading}>
              {saveLoading ? <ClipLoader color="#fff" size={16} /> : 'Save'}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
